/**
 * TeachersTab – the school's teachers by approval status, with a drawer to
 * approve, assign classes, or revoke. Replaces the old /school/approvals page.
 */

import { useMemo } from 'react';
import { DataTable, type Column } from '../analytics/DataTable';
import { AnalyticsEmptyState, AnalyticsPanel, TableSkeleton } from '../analytics/AnalyticsPanel';
import type { ApprovalStatus, RosterPerson, SchoolRoster } from '../../hooks/useSchoolRoster';
import { StatusBadge, TeacherDrawer } from './TeacherDrawer';

const FILTERS: Array<{ key: ApprovalStatus; label: string }> = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

/** A teacher with no status has not been decided on, so they wait with the pending ones. */
const statusOf = (t: RosterPerson): ApprovalStatus => t.approvalStatus ?? 'pending';

export function TeachersTab({
  roster,
  schoolId,
  openId,
  onOpen,
  status,
  onStatus,
}: {
  roster: SchoolRoster;
  schoolId: string;
  /** The teacher whose drawer is open, from the URL. */
  openId: string | null;
  onOpen: (id: string | null) => void;
  /** The status list shown, from the URL; null picks one. */
  status: string | null;
  onStatus: (status: ApprovalStatus) => void;
}) {
  const counts = useMemo(() => {
    const c: Record<ApprovalStatus, number> = { pending: 0, approved: 0, rejected: 0 };
    roster.teachers.forEach((t) => (c[statusOf(t)] += 1));
    return c;
  }, [roster.teachers]);

  // Open on whatever needs a decision; otherwise the working list.
  const chosen = FILTERS.find((f) => f.key === status)?.key;
  const active: ApprovalStatus = chosen ?? (counts.pending > 0 ? 'pending' : 'approved');
  const open = openId ? roster.teacherById.get(openId) ?? null : null;

  const rows = roster.teachers.filter((t) => statusOf(t) === active);

  const classNames = (t: RosterPerson, map: Map<string, { class_name: string; section?: string }[]>) =>
    (map.get(t.id) ?? []).map((c) => `${c.class_name}${c.section ? ` ${c.section}` : ''}`);

  const columns: Column<RosterPerson>[] = [
    {
      key: 'name',
      header: 'Teacher',
      sortValue: (t) => t.name,
      cell: (t) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{t.name}</p>
          <p className="truncate text-xs text-muted-foreground">{t.email}</p>
        </div>
      ),
    },
    {
      key: 'subjects',
      header: 'Subjects',
      hideOnMobile: true,
      cell: (t) => <span className="text-muted-foreground">{t.subjects.length ? t.subjects.join(', ') : '—'}</span>,
    },
    ...(active === 'approved'
      ? ([
          {
            key: 'classes',
            header: 'Classes',
            sortValue: (t) => roster.classesByTeacher.get(t.id)?.length ?? 0,
            cell: (t) => {
              const names = classNames(t, roster.classesByTeacher);
              return names.length ? (
                <div className="flex flex-wrap gap-1">
                  {names.map((n) => (
                    <span key={n} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-foreground">
                      {n}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-amber-600 dark:text-amber-400">No classes</span>
              );
            },
          },
          {
            key: 'lead',
            header: 'Class teacher of',
            hideOnMobile: true,
            cell: (t) => <span className="text-muted-foreground">{classNames(t, roster.classTeacherOf).join(', ') || '—'}</span>,
          },
        ] as Column<RosterPerson>[])
      : []),
    {
      key: 'joined',
      header: 'Signed up',
      align: 'right',
      hideOnMobile: true,
      sortValue: (t) => t.createdAt,
      cell: (t) => <span className="tabular-nums text-muted-foreground">{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      align: 'right',
      cell: (t) => <StatusBadge status={t.approvalStatus} />,
    },
  ];

  return (
    <AnalyticsPanel
      title="Teachers"
      description="Approve teachers who signed up to your school and give them their classes."
      action={
        <div role="tablist" aria-label="Filter teachers by status" className="inline-flex rounded-lg border border-border p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={active === f.key}
              onClick={() => onStatus(f.key)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                active === f.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label} <span className="tabular-nums opacity-80">({counts[f.key]})</span>
            </button>
          ))}
        </div>
      }
    >
      {roster.loading ? (
        <TableSkeleton />
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(t) => t.id}
          initialSort={active === 'pending' ? { key: 'joined', direction: 'asc' } : { key: 'name', direction: 'asc' }}
          searchPlaceholder="Search name, email or subject"
          searchText={(t) => `${t.name} ${t.email} ${t.subjects.join(' ')}`}
          onRowClick={(t) => onOpen(t.id)}
          empty={
            <AnalyticsEmptyState
              compact
              title={active === 'pending' ? 'No teachers waiting for approval' : `No ${active} teachers`}
              description={
                active === 'pending'
                  ? 'Teachers who sign up with your school code appear here for you to approve.'
                  : 'Teachers appear here once you decide on them.'
              }
            />
          }
        />
      )}
      {open && <TeacherDrawer key={open.id} teacher={open} roster={roster} schoolId={schoolId} onClose={() => onOpen(null)} />}
    </AnalyticsPanel>
  );
}
